import React, { useEffect } from 'react';
import AppBar from '@material-ui/core/AppBar';
import CssBaseline from '@material-ui/core/CssBaseline';
import Drawer from '@material-ui/core/Drawer';
import Box from '@material-ui/core/Box';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Toolbar from '@material-ui/core/Toolbar';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import SiteLocation from './Location';
import HeaderNav from './HeaderNav';
import AppIcons from './AppIcons';
import Sidebar from 'components/Sidebar';
import clsx from 'clsx';
import ariaIcon from '../../assets/images/ariaLogo.svg';
import { faArrowAltFromLeft, faArrowAltFromRight } from '@fortawesome/pro-light-svg-icons';
import { Icon } from '@mitie/material-ui-extensions';
import { useMediaQuery } from '@material-ui/core';

const drawerWidth = 260;

export default function PermanentAppBar({ isUserLoggedIn }: { isUserLoggedIn: boolean | null }) {
	const [open, setOpen] = React.useState(true);
	const useStyles = makeStyles((theme: Theme) =>
		createStyles({
			root: {
				display: 'flex',
			},
			closeButtonContainer: {
				position: 'absolute',
				display: 'flex',
				justifyContent: 'center',
				left: 0,
				right: 0,
				bottom: '60px',
				marginBottom: '10px',
				maxWidth: '260px',
			},
			closeButtonCollapsed: {
				zIndex: 1,
				background: '#FFFFFF 0% 0% no-repeat padding-box',
				border: '1px solid #007DBA',
				minWidth: '42px',
				height: '40px',
				margin: 'auto',
				borderRadius: '4px',
				font: 'normal normal normal 16px/12px Roboto',
				letterSpacing: '0px',
				'& .MuiButton-label': {
					'& > * svg': {
						color: '#007DBA',
						width: '18px',
						height: '18px',
						letterSpacing: '0px',
						textAlign: 'left',
						opacity: 1,
						font: 'normal normal 300 18px/12px Font Awesome 5 Pro',
					},
				},
				'&:focus-visible': {
					border: '2px solid #002855',
					background: '#FFFFFF !important',
					'& .MuiButton-label': {
						'& > * svg': {
							color: '#007DBA',
							opacity: 1,
							height: '18px',
							letterSpacing: '0px',
							textAlign: 'left',
						},
					},
				},
				'&:hover': {
					backgroundColor: '#007dba',
					'& .MuiButton-label': {
						'& > * svg': {
							color: '#FFFFFF',
							opacity: 1,
							height: '18px',
							letterSpacing: '0px',
							textAlign: 'left',
						},
					},
				},
				'@media(max-width:1024px)': {
					font: 'normal normal normal 10px/6px Roboto',
					'& svg': {
						height: !open ? '' : '11.2px',
						width: !open ? '' : '11.2px !important',
					},
				},
			},
			closeButtonExpanded: {
				display: 'flex',
				justifyContent: 'flex-start',
				margin: 'auto',
				zIndex: 1,
				background: '#FFFFFF 0% 0% no-repeat padding-box',
				border: '1px solid #007DBA',
				borderRadius: '4px',
				font: 'normal normal normal 16px/12px Roboto',
				letterSpacing: '0px',
				color: '#007DBA',
				width: '228px',
				height: '40px',
				padding: '6px 16px !important',
				'& .MuiButton-label': {
					'& > * svg': {
						color: '#007DBA',
						width: '18px',
						height: '18px',
						letterSpacing: '0px',
						textAlign: 'left',
						opacity: 1,
						font: 'normal normal 300 18px/12px Font Awesome 5 Pro',
					},
				},
				'&:focus-visible': {
					border: '2px solid #002855',
					background: '#FFFFFF !important',
					'& .MuiButton-label': {
						color: '#007DBA',
						'& > * svg': {
							color: '#007DBA',
							opacity: 1,
							height: '18px',
							letterSpacing: '0px',
							textAlign: 'left',
						},
					},
				},
				'&:hover': {
					backgroundColor: '#007dba',
					color: '#FFFFFF !important',
					'& .MuiButton-label': {
						'& > * svg': {
							color: '#FFFFFF',
							opacity: 1,
							height: '18px',
							letterSpacing: '0px',
							textAlign: 'left',
						},
					},
				},
				'@media(max-width:1024px)': {
					font: 'normal normal normal 10px/6px Roboto',
					width: '90%',
					height: '30px',
					'& svg': {
						height: !open ? '' : '11.2px',
						width: !open ? '' : '11.2px !important',
					},
				},
			},

			drawer: {
				width: drawerWidth,
				flexShrink: 0,
				whiteSpace: 'nowrap',
				'@media(max-width:1024px)': {
					width: '180px',
				},
			},
			miniDrawer: {
				width: 70,
				flexShrink: 0,
				whiteSpace: 'nowrap',
			},
			appBar: {
				width: `calc(100% - ${drawerWidth}px)`,
				marginLeft: '400px',
				backgroundColor: theme.palette.common.white,
				boxShadow: '-8px 0px 8px #327CA129',
				borderRadius: '24px 0px 0px 0px',
				opacity: 1,
				'@media(max-width:1024px)': {
					width: `calc(100% - 180px)`,
				},
			},
			miniAppBar: {
				width: `calc(100% - 65px)`,
				backgroundColor: theme.palette.common.white,
				boxShadow: '-8px 0px 8px #327CA129',
				borderRadius: '24px 0px 0px 0px',
				opacity: 1,
			},
			appBarHome: {
				[theme.breakpoints.up('sm')]: {
					width: `calc(100%)`,
				},
				backgroundColor: theme.palette.common.white,
				opacity: 1,
				boxShadow: '-8px 0px 8px #327CA129',
			},
			logo: {
				left: '20px',
				top: '12px',
				opacity: 1,
				position: 'fixed',
				zIndex: 9999,
				'&:focus-visible': {
					border: '2px solid #002855',
					outline: 'none',
				},
			},
			miniLogo: {
				left: '12px',
				top: '12px',
				opacity: 1,
				position: 'fixed',
				zIndex: 9999,
			},
			drawerOpen: {
				width: drawerWidth,
				marginTop: '63px',
				overflowX: 'hidden',
				transition: theme.transitions.create('width', {
					easing: theme.transitions.easing.sharp,
					duration: theme.transitions.duration.enteringScreen,
				}),
				'@media(max-width:1024px)': {
					width: '180px',
				},
			},
			drawerClose: {
				marginTop: '63px',
				transition: theme.transitions.create('width', {
					easing: theme.transitions.easing.sharp,
					duration: theme.transitions.duration.leavingScreen,
				}),
				overflowX: 'hidden',
				width: theme.spacing(8) + 1,
			},
			toolbar: { position: 'fixed' },
			sidebar: {
				background: '#FFFFFF 0% 0% no-repeat padding-box',
				boxShadow: 'inset -8px -2px 8px #327CA129',
				opacity: 1,
				top: '64px',
				left: '0px',
				height: '100%',
				width: '260px',
				overflow: 'hidden',
				paddingTop: '10px',
				'@media(max-width:1024px)': {
					width: '100%',
				},
			},

			miniSidebar: {
				background: '#FFFFFF 0% 0% no-repeat padding-box',
				boxShadow: 'inset -8px -2px 8px #327CA129',
				opacity: 1,
				top: '70px',
				left: '0px',
				height: '100%',
				width: '55px',
				overflow: 'hidden',
				paddingTop: '10px',
				marginLeft: '10px',
			},
			grow: {
				flexGrow: 1,
			},
			appIcon: {
				width: '40px',
				height: '40px',
			},
			iconMargin: {
				marginRight: '8px',
			},
			iconExpanded: {
				display: 'inline-flex',
				minWidth: 'auto',
				width: '32px',
				'@media(max-width:1024px)': {
					width: '18px',
					'& svg': {
						width: '11.2px !important',
						height: '11.2px',
					},
				},
			},
		}),
	);
	const matchesIpad = useMediaQuery('(max-width:768px)');
	const classes = useStyles();

	useEffect(() => {
		if (!localStorage.getItem('miniDrawer')) {
			setOpen(!matchesIpad);
		} else {
			const openState = localStorage.getItem('miniDrawer') === 'true' ? true : false;
			setOpen(openState);
		}
	}, [matchesIpad]);

	const handleDrawerOpen = () => {
		localStorage.setItem('miniDrawer', 'true');
		setOpen(true);
	};

	const handleDrawerClose = () => {
		localStorage.setItem('miniDrawer', 'false');
		setOpen(false);
	};

	const drawer = (
		<div className={open ? classes.sidebar : classes.miniSidebar}>
			<div className={classes.toolbar} />
			<Box className={open ? classes.logo : classes.miniLogo}>
				{open ? (
					<AppIcons alt="Aria Logo" tabIndex={0} />
				) : (
					<Avatar
						variant="rounded"
						src={ariaIcon}
						alt="Aria Icon"
						aria-label="Aria Icon"
						className={`${classes.appIcon}`}
					></Avatar>
				)}
			</Box>
			<Sidebar drawerOpen={open} />
			<div className={classes.closeButtonContainer}>
				{open ? (
					<Button disableFocusRipple className={classes.closeButtonExpanded} onClick={handleDrawerClose}>
						<>
							<Icon icon={faArrowAltFromRight} className={classes.iconExpanded} />
							<span>Close Menu</span>
						</>
					</Button>
				) : (
					<Button
						disableFocusRipple
						title="Expand Menu"
						className={classes.closeButtonCollapsed}
						onClick={handleDrawerOpen}
					>
						<Icon icon={faArrowAltFromLeft} />
					</Button>
				)}
			</div>
		</div>
	);

	return (
		<div className={classes.root}>
			<CssBaseline />
			{isUserLoggedIn ? (
				<>
					<AppBar position="fixed" className={open ? classes.appBar : classes.miniAppBar}>
						<Toolbar>
							<SiteLocation />
							<div className={classes.grow} />

							<HeaderNav />
						</Toolbar>
					</AppBar>
					<nav className={open ? classes.drawer : classes.miniDrawer} aria-label="mailbox folders">
						<Drawer
							className={clsx(classes.drawer, {
								[classes.drawerOpen]: open,
								[classes.drawerClose]: !open,
							})}
							classes={{
								paper: clsx({
									[classes.drawerOpen]: open,
									[classes.drawerClose]: !open,
								}),
							}}
							variant="permanent"
						>
							{drawer}
						</Drawer>
					</nav>
				</>
			) : (
				<>
					<AppBar position="fixed" className={classes.appBarHome}>
						<Toolbar>
							<AppIcons tabIndex={0} />
						</Toolbar>
					</AppBar>
				</>
			)}
		</div>
	);
}
